import React, { useState } from "react";
import { ArrowRight, ArrowLeft, User, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BaseApi from "../services/base-api";
import { useToast } from "../context/ToastContext";
import { useTranslation } from "react-i18next";
import { APP_NAME } from "../constants/info";
import AppLogo from "../components/AppLogo";

const ForgotPassword = () => {
  const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isMobile = (value: string) => /^[0-9]{10,15}$/.test(value);

  const { success, err } = useToast();

  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const validateForm = () => {
    if (!identifier) {
      setError(t("Email or mobile number is required"));
      return false;
    } else if (!isEmail(identifier) && !isMobile(identifier)) {
      setError(t("Enter a valid email or mobile number"));
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);

      try {
        const res = await BaseApi.post("/auth/forgot-password", {
          identifier,
        });
        // console.log("Forgot password res =====> ", res);
        if (res && res?.success) {
          setIsSuccess(true);
          success(t("You have sent you reset link"));
        } else {
          err(t("Failed to send reset link"));
        }
      } catch (error) {
        setError(t("Something went wrong. Please try again."));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
      <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-0 min-h-screen lg:min-h-[600px]">
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }
          @keyframes checkmark {
            0% {
              transform: scale(0) rotate(45deg);
            }
            50% {
              transform: scale(1.2) rotate(45deg);
            }
            100% {
              transform: scale(1) rotate(45deg);
            }
          }
          @keyframes successFade {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}</style>

        {/* Left Side - Branding Section */}
        <div
          className="hidden lg:flex bg-gradient-to-br from-black via-gray-700 to-black p-12 flex-col justify-center items-center relative overflow-hidden"
          style={{
            animation: "slideInLeft 0.8s ease-out",
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div
              className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"
              style={{ animation: "float 6s ease-in-out infinite" }}
            ></div>
            <div
              className="absolute top-40 right-20 w-20 h-20 border-2 border-white rounded-full"
              style={{ animation: "float 8s ease-in-out infinite 1s" }}
            ></div>
            <div
              className="absolute bottom-20 left-20 w-40 h-40 border-2 border-white rounded-full"
              style={{ animation: "float 7s ease-in-out infinite 2s" }}
            ></div>
            <div
              className="absolute bottom-40 right-10 w-24 h-24 border-2 border-white rounded-full"
              style={{ animation: "float 9s ease-in-out infinite 1.5s" }}
            ></div>
          </div>

          <div className="relative z-10 text-center">
            {/* Logo */}
            <div
              className="flex justify-center mb-8"
              style={{
                animation: "pulse 2s ease-in-out infinite",
              }}
            >
              <AppLogo className="w-32 h-32 bg-white rounded-2xl lg-shadow" />
            </div>

            <h1 className="text-5xl font-bold text-white mb-4">{APP_NAME}</h1>
            <p className="text-gray-300 text-lg mb-8 max-w-md">
              {t(
                "Don't worry! We'll help you reset your password and get back to shopping.",
              )}
            </p>

            {/* Features */}
            <div className="space-y-4 text-left max-w-md">
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Secure Process")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Your account security is our top priority")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Quick Recovery")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("Reset your password in just a few steps")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white/10 rounded-lg p-2 mt-1">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">
                    {t("Email Verification")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("We'll send you a secure reset link")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div
          className="flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-white"
          style={{
            animation: "slideInRight 0.8s ease-out",
          }}
        >
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <AppLogo className="w-24 h-24 bg-white rounded-2xl lg-shadow" />
            </div>

            {/* Back Button */}
            <button
              onClick={() => navigate("/signin")}
              className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-6 group"
              style={{
                animation: "slideInRight 0.5s ease-out 0.1s backwards",
              }}
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">{t("Back to Sign In")}</span>
            </button>

            {!isSuccess ? (
              <>
                <div className="mb-8">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                    {t("Forgot Password?")}
                  </h2>
                  <p className="text-gray-600 text-base sm:text-lg">
                    {t(
                      "No worries! Enter your email or mobile number and we'll send you reset instructions.",
                    )}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email/Mobile Field */}
                  <div
                    style={{
                      animation: "slideInRight 0.5s ease-out 0.2s backwards",
                    }}
                  >
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t("Email or Mobile Number")}
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={identifier}
                        onChange={(e) => {
                          setIdentifier(e.target.value);
                          if (error) {
                            setError("");
                          }
                        }}
                        className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 ${
                          error
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 focus:border-black"
                        } focus:outline-none transition-all duration-300 text-base`}
                        placeholder={t("Enter email or mobile number")}
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                        <span className="text-lg">•</span> {error}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div
                    style={{
                      animation: "slideInRight 0.5s ease-out 0.3s backwards",
                    }}
                  >
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full bg-black text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 text-base ${
                        isLoading
                          ? "opacity-70 cursor-not-allowed"
                          : "hover:bg-gray-700 hover:shadow-2xl transform hover:-translate-y-1 active:scale-95"
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <div
                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                            style={{
                              animation: "spin 1s linear infinite",
                            }}
                          ></div>
                          <span>{t("Sending...")}</span>
                        </>
                      ) : (
                        <>
                          <span>{t("Send Reset Link")}</span>
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Info Box */}
                <div
                  className="mt-6 bg-[#0d223f] border-2 border-[#0d223f] rounded-xl p-4"
                  style={{
                    animation: "slideInRight 0.5s ease-out 0.4s backwards",
                  }}
                >
                  <div className="flex gap-3">
                    <svg
                      className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-blue-900 text-sm mb-1">
                        {t("What happens next?")}
                      </h4>
                      <p className="text-blue-800 text-sm leading-relaxed">
                        {t("send reset link message")}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Success State */
              <div
                className="text-center"
                style={{
                  animation: "successFade 0.6s ease-out",
                }}
              >
                <div className="mb-6 flex justify-center">
                  <div className="bg-green-100 rounded-full p-4">
                    <CheckCircle className="w-16 h-16 text-green-600" />
                  </div>
                </div>

                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  {t("Check Your Inbox")}
                </h2>
                <p className="text-gray-600 text-base sm:text-lg mb-6">
                  {t("We've sent a password reset link to")}
                </p>
                <p className="text-black font-semibold text-lg mb-8">
                  {identifier}
                </p>

                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 mb-6">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {t("reset email link")}
                  </p>
                </div>

                <p className="text-sm text-gray-500">
                  {t("Redirecting you to sign in page in a moment...")}
                </p>
              </div>
            )}

            {/* Footer Text */}
            {!isSuccess && (
              <div
                className="text-center mt-8"
                style={{
                  animation: "slideInRight 0.5s ease-out 0.5s backwards",
                }}
              >
                <p className="text-sm text-gray-600">
                  {t("Remember your password?")}{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/signin")}
                    className="font-semibold text-black hover:text-gray-700 transition-colors underline decoration-2 underline-offset-2"
                  >
                    {t("Sign In")}
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
